import { useCallback, useEffect, useState, createContext, ReactNode, useContext, useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithStorage, RESET } from 'jotai/utils';

import { UTMSourceType } from '@restworld/data-services';

import { useNavigationContext } from './navigation-context';

const UTMSourceContext = createContext<{
  utmSource: UTMSourceType;
  refCode: string | null;
  partnerCode: string | null;
  removeRefCode: () => void;
  removeUtmSource: () => void;
  removePartnerCode: () => void;
}>({
  utmSource: null,
  refCode: null,
  partnerCode: null,
  removeRefCode: () => null,
  removeUtmSource: () => null,
  removePartnerCode: () => null,
});
const UTM_SOURCE_STORAGE_KEY = 'utm_source';
const PARTNER_CODE_STORAGE_KEY = 'partner_code';

const utmSourceAtom = atomWithStorage<UTMSourceType>(UTM_SOURCE_STORAGE_KEY, null);
const partnerCodeAtom = atomWithStorage<string | null>(PARTNER_CODE_STORAGE_KEY, null);

const UTMSourceProvider = ({ children }: { children: ReactNode }) => {
  const [utmSource, setUtmSource] = useAtom(utmSourceAtom);
  const [partnerCode, setPartnerCode] = useAtom(partnerCodeAtom);
  const [refCode, setRefCode] = useState<string | null>(null);
  const { router } = useNavigationContext();
  const query = router?.['query'];
  const queryRefCode = query?.ref_code;
  const queryUtmSource = query?.utm_source;
  const queryPartner = query?.partner;
  const events = router?.events;
  const isRouteReady = router?.isReady;
  const isQueryUTMSourceValid = useMemo(() => typeof queryUtmSource === 'string' && !!queryUtmSource, [queryUtmSource]);
  const isQueryPartnerValid = useMemo(() => typeof queryPartner === 'string' && !!queryPartner, [queryPartner]);

  const saveUTMSource = useCallback(() => {
    if (!isQueryUTMSourceValid) return;
    if (queryUtmSource) setUtmSource(queryUtmSource);
  }, [isQueryUTMSourceValid, queryUtmSource, setUtmSource]);

  const savePartnerCode = useCallback(() => {
    if (!isQueryPartnerValid) return;
    if (typeof queryPartner === 'string') setPartnerCode(queryPartner);
  }, [isQueryPartnerValid, queryPartner, setPartnerCode]);

  const saveReferralCode = useCallback(() => {
    const urlRefCode = queryRefCode && Array.isArray(queryRefCode) ? queryRefCode[0] : queryRefCode;
    const localstorageRefCode = window.localStorage.getItem('ref_code');

    if (urlRefCode) {
      window.localStorage.setItem('ref_code', urlRefCode);
      setRefCode(urlRefCode);
    } else if (localstorageRefCode) {
      setRefCode(localstorageRefCode);
    }
  }, [queryRefCode]);

  useEffect(() => {
    if (!events) return;
    events.on('routeChangeStart', saveUTMSource);
    events.on('routeChangeStart', savePartnerCode);
    return () => {
      events.off('routeChangeStart', saveUTMSource);
      events.off('routeChangeStart', savePartnerCode);
    };
  }, [events, saveUTMSource, savePartnerCode]);

  useEffect(() => {
    if (isRouteReady) {
      saveUTMSource();
      savePartnerCode();
      saveReferralCode();
    }
  }, [isRouteReady, saveUTMSource, savePartnerCode, saveReferralCode]);

  const removeRefCode = useCallback(() => {
    window.localStorage.removeItem('ref_code');
    setRefCode(null);
  }, []);

  const removeUtmSource = useCallback(() => {
    setUtmSource(RESET);
  }, [setUtmSource]);

  const removePartnerCode = useCallback(() => {
    setPartnerCode(RESET);
  }, [setPartnerCode]);

  return (
    <UTMSourceContext.Provider
      value={{ utmSource, refCode, partnerCode, removeRefCode, removeUtmSource, removePartnerCode }}
    >
      {children}
    </UTMSourceContext.Provider>
  );
};

const useUTMSource = () => {
  const context = useContext(UTMSourceContext);
  if (!context) throw new Error('useUTMSource must be used within a UTMSourceContext');
  return useMemo(() => context, [context]);
};

export { UTMSourceProvider, useUTMSource };
